interface LocatedSensor {
  id: string;
  latitude: number;
  longitude: number;
}

const MAX_LOCATION_DISTANCE_METERS = 50;
const EARTH_RADIUS_METERS = 6371000;

export interface DisplaySensor<T extends LocatedSensor> {
  sensor: T;
  useOriginalPosition: boolean;
}

const protocol = (sensor: LocatedSensor) => sensor.id.split("/", 1)[0];
const distance = (left: LocatedSensor, right: LocatedSensor) => {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const latitudeDelta = toRadians(right.latitude - left.latitude);
  const longitudeDelta = toRadians(right.longitude - left.longitude);
  const leftLatitude = toRadians(left.latitude);
  const rightLatitude = toRadians(right.latitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(leftLatitude) *
      Math.cos(rightLatitude) *
      Math.sin(longitudeDelta / 2) ** 2;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(haversine));
};

const duplicateGroups = <T extends LocatedSensor>(sensors: T[]) => {
  const candidates = sensors.filter(
    (sensor) =>
      ["alert1", "alert2"].includes(protocol(sensor)) &&
      (sensor.latitude !== 0 || sensor.longitude !== 0)
  );
  const parents = candidates.map((_, index) => index);
  const find = (index: number): number => {
    if (parents[index] !== index) {
      parents[index] = find(parents[index]);
    }
    return parents[index];
  };
  const union = (left: number, right: number) => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) {
      parents[rightRoot] = leftRoot;
    }
  };

  candidates.forEach((sensor, index) => {
    candidates.slice(0, index).forEach((candidate, candidateIndex) => {
      if (distance(sensor, candidate) <= MAX_LOCATION_DISTANCE_METERS) {
        union(index, candidateIndex);
      }
    });
  });

  const groups = new Map<number, T[]>();
  candidates.forEach((sensor, index) => {
    const root = find(index);
    groups.set(root, [...(groups.get(root) || []), sensor]);
  });

  return Array.from(groups.values()).filter(
    (group) =>
      group.some((sensor) => protocol(sensor) === "alert1") &&
      group.some((sensor) => protocol(sensor) === "alert2")
  );
};

export const excludeDuplicateAlert1Sensors = <T extends LocatedSensor>(
  sensors: T[]
) => {
  const hiddenIds = new Set(
    duplicateGroups(sensors)
      .flatMap((group) => group)
      .filter((sensor) => protocol(sensor) === "alert1")
      .map((sensor) => sensor.id)
  );

  return sensors.filter((sensor) => !hiddenIds.has(sensor.id));
};

export const collapseDuplicateSensors = <T extends LocatedSensor>(
  sensors: T[],
  compareAlert2: (left: T, right: T) => number = (left, right) =>
    left.id.localeCompare(right.id)
): DisplaySensor<T>[] => {
  const groups = duplicateGroups(sensors);

  const preferredIds = new Set<string>();
  const hiddenIds = new Set<string>();
  groups.forEach((group) => {
    const alert2 = group
      .filter((sensor) => protocol(sensor) === "alert2")
      .sort(compareAlert2);

    preferredIds.add(alert2[0].id);
    group
      .filter((sensor) => sensor.id !== alert2[0].id)
      .forEach((sensor) => hiddenIds.add(sensor.id));
  });

  return sensors
    .filter((sensor) => !hiddenIds.has(sensor.id))
    .map((sensor) => ({
      sensor,
      useOriginalPosition: preferredIds.has(sensor.id),
    }));
};